import dayjsBase, { type ConfigType, type Dayjs, extend, locale } from 'dayjs';
import ja from 'dayjs/locale/ja';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import localeData from 'dayjs/plugin/localeData';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { SupportedLanguage } from '@/__generated__/i18n';

import { isServer, SERVER_TIME_ZONE } from './timeZone';

locale(ja);
extend(isSameOrAfter);
extend(isBetween);
extend(duration);
extend(localeData);
extend(relativeTime);
extend(utc);
extend(timezone);

const OFFSET_SUFFIX_PATTERN = /\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?\s*(?:z|[+-]\d{2}(?::?\d{2})?)$/iu;

/**
 * `dayjs` that works in `SERVER_TIME_ZONE` on the server and in the browser's time zone in the browser, so that
 * server-rendered dates match what Japanese browsers hydrate.
 */
export const dayjs: ((date?: ConfigType) => Dayjs) & Omit<typeof dayjsBase, never> = isServer
  ? Object.assign(toServerDayjs, dayjsBase)
  : dayjsBase;

function toServerDayjs(date?: ConfigType): Dayjs {
  // `dayjs.tz` reads every string as wall-clock time in the zone, even one with an offset.
  if (typeof date === 'string' && !OFFSET_SUFFIX_PATTERN.test(date)) return dayjsBase.tz(date, SERVER_TIME_ZONE);
  const parsed = dayjsBase(date);
  return parsed.isValid() ? parsed.tz(SERVER_TIME_ZONE) : parsed;
}

const DAYJS_LOCALE_BY_APP_LOCALE = {
  'en-US': 'en',
  'ja-JP': 'ja',
  'zh-CN': 'zh-cn',
  'zh-TW': 'zh-tw',
} satisfies Record<SupportedLanguage, string>;

export function getLocalizedWeekdays(appLocale: SupportedLanguage): string[] {
  return dayjs().locale(getDayjsLocale(appLocale)).localeData().weekdays();
}

export function humanizeDuration(durationMs: number, appLocale: SupportedLanguage, withSuffix = false): string {
  return dayjs.duration(durationMs).locale(getDayjsLocale(appLocale)).humanize(withSuffix);
}

export function formatFromNow(date: Date, appLocale: SupportedLanguage): string {
  return dayjs(date).locale(getDayjsLocale(appLocale)).fromNow();
}

function getDayjsLocale(appLocale: SupportedLanguage): string {
  return DAYJS_LOCALE_BY_APP_LOCALE[appLocale];
}
